import React, { useState, useEffect, SyntheticEvent, useRef } from "react";
import AdminLayout from "../../layout/AdminLayout";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Divider,
  Alert,
  Tabs,
  Tab,
  Typography,
  Grid
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DeleteIcon from "@mui/icons-material/Delete";
import "dayjs/locale/tr";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import AdminField from "../../layout/AdminField";
import {
  initializeNewCampaign,
  initializeCampaignData,
} from "../../utils/initializeCampaign";
import { v4 as uuid } from "uuid";
import agent from "../../api/agent";
import { toast } from "react-toastify";
import ImagePickerModal from "../../layout/ImagePickerModal";
import VideoPickerModal from "../../layout/VideoPickerModal";
import Swal from "sweetalert2";
import { basename } from "../../utils/env";
import CampaignFields from "./main/CampaignFields";
import FormCustomization from "./main/FormCustomization";
import ScreenStyleSettings from "./main/ScreenStyleSettings";
import { Dayjs } from "dayjs";

export default function CatchGameCampaignDetails() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<any | null>(null);
  const [titleInput, setTitleInput] = useState<string>("");
  const [tabValue, setTabValue] = useState(0);
  const [errors, setErrors] = useState<any>({});
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [openVideoModal, setOpenVideoModal] = useState<boolean>(false);
  const [activeVideoField, setActiveVideoField] = useState<string | null>(null);

  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [iframeSize, setIframeSize] = useState({ width: 360, height: 640 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [submitType, setSubmitType] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);

  const navigate = useNavigate();
  const gameScreenUrl = `${basename}/game/${id || campaign?.id}?gameType=${campaign?.gameType || 7}`;

  useEffect(() => {
    if (id) {
      agent.Campaigns.details(id).then((response) => {
        const campaignData: any = response;
        setCampaign(initializeCampaignData(campaignData));
        setTitleInput(campaignData.title);
        console.log(campaignData);
      });
    } else {
      const newCampaign = initializeNewCampaign();
      setCampaign(newCampaign);
      setTitleInput(newCampaign.title);
    }
  }, [id]);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, [submitType]);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFieldChange = (field: string, value: any) => {
    setIsDirty(false);
    setCampaign({ ...campaign, [field]: value });
  };

  const handleScreenStyleChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      screenStyle: { ...campaign.screenStyle, [field]: value },
    });
  };

  const handleCatchGameChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      catchGame: { ...campaign.catchGame, [field]: value },
    });
  };

  const handleFormStateChange = (field: string, value: any) => {
    if (!campaign) return;

    setIsDirty(false);

    setCampaign({
      ...campaign,
      formState: { ...campaign.formState, [field]: value },
    });
  };

  const validateForm = () => {
    let tempErrors: any = {};

    if (!titleInput) tempErrors.titleInput = "Kampanya Başlığı zorunludur";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isDirty) {
      // If there are no changes, don't resubmit or show error
      if (submitType === "regularSubmit") {
        navigate("/campaigns", { state: { reload: true } });
      }
      return;
    }

    const campaignId = campaign?.id || uuid();
    const screenStyleId = campaign?.screenStyle.id || uuid();
    const catchGameId = campaign?.catchGame.id || uuid();
    const formStateId = campaign?.formState.id || uuid();

    const campaignData = {
      ...campaign,
      id: campaignId,
      gameType: 7,
      title: titleInput,
      startDate: (campaign!.startDate as Dayjs).toISOString(),
      endDate: (campaign!.endDate as Dayjs).toISOString(),
      collectUserData:
          campaign!.collectUserData === 1
            ? 1
            : campaign!.collectUserData === 2
              ? 2
              : campaign!.collectUserData === 3
                ? 3
                : 4,
      screenStyle: {
        ...campaign!.screenStyle,
        id: screenStyleId,
        campaignId: campaignId,
      },
      formState: {
        ...campaign!.formState,
        id: formStateId,
        campaignId: campaignId,
      },
      wheel: {},
      puzzle: {},
      scratchWin: {},
      touchWin: {},
      matchWin: {},
      quiz: {},
      catchGame: {
        ...campaign!.catchGame,
        id: catchGameId,
        campaignId: campaignId
      }
    };

    try {
      if (id) {
        await agent.Campaigns.update(campaignData);
      } else {
        console.log(campaignData);
        await agent.Campaigns.create(campaignData);
      }
      if (submitType === "regularSubmit") {
        navigate("/campaigns", { state: { reload: true } });
      } else {
        setIsDirty(true);
      }
    } catch (error: any) {
      if (error.response) {
        console.error("Error response data:", error.response.data);
      } else {
        console.error("Kampanya düzenlenirken bir hata oluştu:", error.message);
      }
    }
    setSubmitType(null);
  };

  const handleShowGameScreen = async () => {
    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    setSubmitType("showGameScreen");

    if (!isLoaded) {
      setIframeSrc(gameScreenUrl);
      setIsLoaded(true);
    } else {
      setIframeSrc(null);
      setTimeout(() => setIframeSrc(gameScreenUrl), 100);
    }
  };

  const handleRegularSubmit = async () => {
    if (!validateForm()) {
      toast.error(
        "Lütfen geri dönüp kırmızıyla işaretlenen zorunlu alanları doldurunuz!"
      );
      return;
    }

    setSubmitType("regularSubmit");
  };

  const openImagePicker = (field: string) => {
    setActiveField(field);
    setOpenModal(true);
  };

  const openVideoPicker = (field: string) => {
    setActiveVideoField(field);
    setOpenVideoModal(true);
  };

  const handleImageSelect = (url: string) => {
    if (!campaign || !activeField) return;

    if (["modalBg", "modalInnerBg"].includes(activeField)) {
      setCampaign({ ...campaign, [activeField]: url });
    } else if (["logo", "screenBg", "screenCenterBg"].includes(activeField)) {
      handleScreenStyleChange(activeField, url);
    } else {
      handleCatchGameChange(activeField, url);
    }

    setOpenModal(false);
  };

  const handleVideoSelect = (url: string) => {
    if (!campaign || !activeVideoField) return;
    setCampaign({ ...campaign, [activeVideoField]: url });
    setOpenVideoModal(false);
  };

  const handleDeleteGameData = () => {
    Swal.fire({
      title: "Silmek istediğinizden emin misiniz?",
      text: "Bu kampanya için kaydedilmiş tüm oyun ve oyuncu verileri silinecektir. Silme işlemini geri alamazsınız!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Tamam",
      cancelButtonText: "İptal",
    }).then((result) => {
      if (result.isConfirmed) {
        agent.Players.deleteAll(id)
          .then(() => {
            toast.success("Tüm oyun ve oyuncu verileri silindi!");
          })
          .catch((error) => {
            toast.error("Veriler silinirken bir hata oluştu.");
          });
      }
    });
  };

  const handleSizeChange = (size: string | number, shouldStartPreview = false) => {
    const parsedSize = Number(size);
    if (!Number.isNaN(parsedSize)) {
      const isLandscape = [1, 3, 5].includes(parsedSize);
      setIframeSize(isLandscape ? { width: 640, height: 360 } : { width: 360, height: 640 });

      if (shouldStartPreview && id) {
        handleShowGameScreen();
      }
      return;
    }

    if (size === "9:16") {
      setIframeSize({ width: 360, height: 640 });
    } else if (size === "16:9") {
      setIframeSize({ width: 640, height: 360 });
    }
  };

  if (!campaign) return null;

  return (
    <AdminLayout title={campaign.title || "Düşenleri Yakala Kampanyası Ekle"}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/campaigns")}
        sx={{ mr: 3 }}
      >
        Kampanyalara Dön
      </Button>
      {id && (
        <>
          <Button
            variant="contained"
            startIcon={<EmojiEventsIcon />}
            color="secondary"
            onClick={() => navigate(`/scoreboard/${id}?campaignTitle=${campaign.title}&gameType=${campaign.gameType}`)}
            sx={{ mr: 2 }}
          >
            Skor Tablosu
          </Button>
          <Button
            variant="contained"
            startIcon={<AnalyticsIcon />}
            color="success"
            onClick={() => navigate(`/statistics/${id}?campaignTitle=${campaign.title}&gameType=${campaign.gameType}`)}
            sx={{ mr: 2 }}
          >
            İstatistikler
          </Button>
          <Button
            variant="contained"
            startIcon={<VideogameAssetIcon />}
            color="warning"
            onClick={() => window.open(gameScreenUrl, "_blank")}
            sx={{ mr: 2 }}
          >
            Oyun Ekranı
          </Button>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            color="error"
            onClick={handleDeleteGameData}
          >
            Oyun ve Oyuncu Verilerini Sil
          </Button>
        </>
      )}
      {campaign.id ? (
        <Alert sx={{ my: 3 }} severity="warning">
          Kampanyanızı oluşturdunuz. Artık <strong>Çark Dilim Alanları</strong>{" "}
          sekmesindeki <strong>Dilim Sayısı</strong> oluşturulmuş kampanya
          içinde değiştirilemez.
        </Alert>
      ) : (
        <Alert sx={{ my: 3 }} severity="info">
          Temel kampanya bilgilerinizi girdikten sonra kaydetmeden önce lütfen
          yan sekmelerdeki bilgileri de düzenleyiniz. Kampanyalarınızı oluşturduktan sonra ödül
          istatistiklerini inceleyebileceğiniz <strong>İstatistikler</strong>{" "}
          butonu hemen yukarıda yer alacaktır.
        </Alert>
      )}
      <Tabs
        value={tabValue}
        className="admin-tabs"
        onChange={handleTabChange}
        aria-label="basic tabs example"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab
          label="Kampanya Alanları"
          id="simple-tab-0"
          aria-controls="simple-tabpanel-0"
        />
        <Tab
          label="Genel Görselleştirme"
          id="simple-tab-1"
          aria-controls="simple-tabpanel-1"
        />
        <Tab
          label="Düşenleri Yakala Alanları"
          id="simple-tab-2"
          aria-controls="simple-tabpanel-2"
        />
        <Tab
          label="Form Özelleştirme"
          id="simple-tab-3"
          aria-controls="simple-tabpanel-3"
        />
      </Tabs>
      <Divider sx={{ mb: 5 }} />
      <form ref={formRef} onSubmit={handleSubmit}>
        <CampaignFields
          campaign={campaign}
          setCampaign={setCampaign}
          errors={errors}
          setErrors={setErrors}
          titleInput={titleInput}
          setTitleInput={setTitleInput}
          handleFieldChange={handleFieldChange}
          openImagePicker={openImagePicker}
          openVideoPicker={openVideoPicker}
          tabValue={tabValue}
        />
        <ScreenStyleSettings
          tabValue={tabValue}
          tabOrder={1}
          campaign={campaign}
          handleScreenStyleChange={handleScreenStyleChange}
          handleSizeChange={handleSizeChange}
          handleShowGameScreen={handleShowGameScreen}
          openImagePicker={openImagePicker}
          id={id}
          iframeSrc={iframeSrc}
          isLoaded={isLoaded}
          iframeSize={iframeSize}
        />
        {campaign.catchGame && (
          <>
            <div
              role="tabpanel"
              hidden={tabValue !== 2}
              id="simple-tabpanel-2"
              aria-labelledby="simple-tab-2"
            >
              <Typography variant="h5" gutterBottom sx={{ mb: 5 }}>
                Düşenleri Yakala Ekranı Ayarları
              </Typography>

              <Divider sx={{ mb: 5 }} />

              <Grid container spacing={6} sx={{ mb: 4 }}>
                <Grid item xs={6}>
                  <Grid container spacing={4} sx={{ mb: 4 }}>
                    <AdminField
                      label="Oyun Alanı Yüksekliği"
                      value={campaign.catchGame.gameHeight}
                      type="number"
                      onChange={(value) =>
                        handleCatchGameChange(
                          "gameHeight",
                          value !== "" ? parseInt(value) : null
                        )
                      }
                      helperText="Düşen obje oyun alanının tepeden zemine yükseklik değerini rakam olarak giriniz."
                    />
                    <AdminField
                      label="Oyun Süresi"
                      value={campaign.catchGame.gameDuration}
                      type="number"
                      onChange={(value) => handleCatchGameChange("gameDuration", value)}
                      helperText="Oyunun süresini dakika cinsinden rakam olarak giriniz"
                    />
                    <AdminField
                      label="Puan Çarpanı"
                      value={campaign.catchGame.pointMultiplier}
                      type="number"
                      onChange={(value) => handleCatchGameChange("pointMultiplier", value)}
                      helperText="Yakalanan obje başına kazanılacak puan çarpanını rakam olarak giriniz"
                    />
                    <AdminField
                      label="Obje Sıklığı (milisaniye)"
                      value={campaign.catchGame.minSpawnGap}
                      type="number"
                      onChange={(value) => handleCatchGameChange("minSpawnGap", value)}
                      helperText="Objelerin birbiri ardına çıkma aralığını milisaniye cinsinden rakam olarak giriniz"
                    />
                    <AdminField
                      label="Düşen Obje Görseli"
                      value={campaign.catchGame.catchObject}
                      onChange={(value) =>
                        handleCatchGameChange("catchObject", value)
                      }
                      helperText="Düşen objenin görselini ekleyiniz"
                      type="button"
                      buttonText="Galeri'den Seç"
                      onClick={() => openImagePicker("catchObject")}
                      error={!!errors.catchObject}
                      errorMessage={errors.catchObject}
                    />
                    <AdminField
                      label="Yakalayıcı Obje Görseli"
                      value={campaign.catchGame.catcher}
                      onChange={(value) =>
                        handleCatchGameChange("catcher", value)
                      }
                      helperText="Zemindeki yakalayıcı objenin görselini ekleyiniz"
                      type="button"
                      buttonText="Galeri'den Seç"
                      onClick={() => openImagePicker("catcher")}
                      error={!!errors.catcher}
                      errorMessage={errors.catcher}
                    />
                    <AdminField
                      label="Zemin Kaplama Görseli"
                      value={campaign.catchGame.floor}
                      onChange={(value) =>
                        handleCatchGameChange("floor", value)
                      }
                      helperText="Eğer kullanacaksanız zeminin kaplama görselini ekleyiniz"
                      type="button"
                      buttonText="Galeri'den Seç"
                      onClick={() => openImagePicker("floor")}
                      error={!!errors.floor}
                      errorMessage={errors.floor}
                    />
                    <AdminField
                      label="Zemin Yüksekliği"
                      value={campaign.catchGame.floorHeight}
                      type="number"
                      onChange={(value) =>
                        handleCatchGameChange(
                          "floorHeight",
                          value !== "" ? parseInt(value) : null
                        )
                      }
                      helperText="Zeminin yükseklik değerini rakam olarak giriniz."
                    />
                    <AdminField
                      label="Kaydırma Çubuğu Rengi"
                      type="color"
                      value={campaign.catchGame.sliderColor}
                      onChange={(value) =>
                        handleCatchGameChange("sliderColor", value)
                      }
                      helperText="Kaydırma çubuğunun rengini seçiniz"
                    />
                  </Grid>
                </Grid>                
              </Grid>
            </div>
          </>
        )}
        <FormCustomization
          tabValue={tabValue}
          tabOrder={3}
          campaign={campaign}
          handleFormStateChange={handleFormStateChange}
        />
        <Divider />
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleRegularSubmit}
          sx={{ my: 4 }}
        >
          Kaydet
        </Button>
      </form>
      <ImagePickerModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSelect={handleImageSelect}
      />
      <VideoPickerModal
        open={openVideoModal}
        onClose={() => setOpenVideoModal(false)}
        onSelect={handleVideoSelect}
      />
    </AdminLayout>
  );
}
